import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { concatMap, map, switchMap } from 'rxjs/operators';
import { forkJoin, Observable, of, pipe, take, combineLatest } from 'rxjs';
import { AttributeListRowModel } from '../models/attribute-list-row.model';
import { Store } from '@ngrx/store';
import { AttributeListColumnModel } from '../models/attribute-list-column.model';
import {
  selectCheckedRowsForSelectedTab,
  selectColumnsForSelectedTab, selectDataForSelectedTab, selectHasNoRowsForSelectedTab, selectLoadErrorForSelectedTab,
  selectLoadingDataSelectedTab, selectPagingDataSelectedTab,
  selectRowCountForSelectedTab,
  selectRowsForSelectedTab, selectSelectedRowIdForSelectedTab, selectSelectedTab, selectSortForSelectedTab,
} from '../state/attribute-list.selectors';
import { AttributeListStateService } from '../services/attribute-list-state.service';
import { AttributeListDataService } from '../services/attribute-list-data.service';
import { BaseComponentTypeEnum, AttributeType, AttributeFilterModel } from '@tailormap-viewer/api';
import { SimpleAttributeFilterService } from '../../../filter/services/simple-attribute-filter.service';
import { AttributeListFilterComponent, FilterDialogData } from '../attribute-list-filter/attribute-list-filter.component';
import { MatDialog } from '@angular/material/dialog';
import { selectViewerId } from '../../../state/core.selectors';
import { CqlFilterHelper } from '../../../filter/helpers/cql-filter.helper';
import { CssHelper, SnackBarMessageComponent, ErrorMessageComponent } from '@tailormap-viewer/shared';
import { AttributeListFeatureDetailsService } from '../services/attribute-list-feature-details.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StatisticsHelper } from '../helpers/statistics-helper';
import { StatisticType } from '../models/attribute-list-api-service.model';
import { AttributeListStatisticsService } from '../services/attribute-list-statistics.service';
import { AttributeListPagingDataType } from '../models/attribute-list-paging-data.type';
import { MatButton } from '@angular/material/button';
import { AttributeListTableComponent } from '../attribute-list-table/attribute-list-table.component';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'tm-attribute-list-content',
    templateUrl: './attribute-list-content.component.html',
    styleUrls: ['./attribute-list-content.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ErrorMessageComponent,
        MatButton,
        AttributeListTableComponent,
        AsyncPipe,
    ],
})
export class AttributeListContentComponent implements OnInit {
  private store$ = inject(Store);
  private attributeListStateService = inject(AttributeListStateService);
  private attributeListDataService = inject(AttributeListDataService);
  private attributeListFeatureDetailsService = inject(AttributeListFeatureDetailsService);
  private attributeListStatisticsService = inject(AttributeListStatisticsService);
  private simpleAttributeFilterService = inject(SimpleAttributeFilterService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);

  public rows$: Observable<AttributeListRowModel[]> = of([]);
  public columns$: Observable<AttributeListColumnModel[]> = of([]);
  public notLoadingData$: Observable<boolean> = of(false);
  public sort$: Observable<{ column: string; direction: string } | null> = of(null);
  public filters$: Observable<AttributeFilterModel[]> = of([]);
  public selectedRowId$: Observable<string | undefined> = of(undefined);
  public errorMessage$: Observable<string | undefined> = of(undefined);
  public hasRows$: Observable<boolean> = of(false);
  public hasNoRows$: Observable<boolean> = of(true);
  public pagingDataSelectedTab$: Observable<AttributeListPagingDataType> = this.store$.select(selectPagingDataSelectedTab);
  public checkedRows$ = this.store$.select(selectCheckedRowsForSelectedTab);

  public canExpandRows$ = this.attributeListFeatureDetailsService.canExpandRows$;
  public canCheckRows$ = this.attributeListFeatureDetailsService.canCheckRows$;
  public featureDetails$ = this.attributeListFeatureDetailsService.featureDetails$;
  public loadingFeatureDetailsIds$ = this.attributeListFeatureDetailsService.loadingFeatureDetailsIds$;

  public showStatistics$ = this.attributeListStatisticsService.canLoadStatistics$;
  public statistics$ = this.attributeListStatisticsService.statistics$;

  public ngOnInit(): void {
    this.errorMessage$ = this.store$.select(selectLoadErrorForSelectedTab);
    this.rows$ = this.store$.select(selectRowsForSelectedTab);
    this.sort$ = this.store$.select(selectSortForSelectedTab);
    this.hasRows$ = this.store$.select(selectRowCountForSelectedTab).pipe(map(rowCount => rowCount > 0));
    this.hasNoRows$ = this.store$.select(selectHasNoRowsForSelectedTab);
    this.columns$ = this.store$.select(selectColumnsForSelectedTab);
    this.notLoadingData$ = this.store$.select(selectLoadingDataSelectedTab).pipe(map(loading => !loading));
    this.filters$ = combineLatest([
      this.store$.select(selectSelectedTab),
      this.store$.select(selectDataForSelectedTab),
    ])
      .pipe(switchMap(([ tab, data ]) => {
        if (!tab || !tab.layerId) {
          return of([]);
        }
        return this.simpleAttributeFilterService.getFilters$(BaseComponentTypeEnum.ATTRIBUTE_LIST, tab.layerId, data?.featureType);
      }));
    this.selectedRowId$ = this.store$.select(selectSelectedRowIdForSelectedTab);
  }

  public onSelectRow(row: { id: string; selected: boolean }): void {
    this.attributeListStateService.executeActionForCurrentData(dataId => {
      this.attributeListDataService.updateRowSelected(dataId, row.id, row.selected);
    });
  }

  public onSortClick(sort: { columnId: string; direction: 'asc' | 'desc' | '' }): void {
    this.attributeListStateService.executeActionForCurrentData(dataId => {
      this.attributeListDataService.updateSort(dataId, sort.columnId, sort.direction);
    });
  }

  public onSetFilter($event: { columnId: string; attributeType: AttributeType }) {
    combineLatest([
      this.store$.select(selectSelectedTab),
      this.store$.select(selectDataForSelectedTab),
      this.store$.select(selectViewerId),
    ])
      .pipe(
        pipe(take(1)),
        concatMap(([ selectedTab, selectedData, applicationId ]) => {
          if (!selectedTab || !selectedTab.layerId) {
            return of(null);
          }
          return forkJoin([
            this.simpleAttributeFilterService.getFilterForAttribute$(
              BaseComponentTypeEnum.ATTRIBUTE_LIST,
              selectedTab.layerId,
              $event.columnId,
              selectedData?.featureType,
            ).pipe(take(1)),
            this.simpleAttributeFilterService.getFiltersExcludingAttribute$(
              BaseComponentTypeEnum.ATTRIBUTE_LIST,
              selectedTab.layerId,
              $event.columnId,
              selectedData?.featureType,
            ).pipe(take(1)),
            of(selectedTab),
            of(selectedData),
            of(applicationId),
            this.columns$.pipe(
              take(1),
              map(columns => columns.find(col => col.id === $event.columnId)?.label || undefined),
            ),
          ]);
        }),
      )
      .subscribe(result => {
        if (!result) {
          return;
        }
        const [ attributeFilterModel, otherFilters, selectedTab, selectedData, applicationId, attributeAlias ] = result;
        if (applicationId === null || !selectedTab.layerId) {
          return;
        }
        const filtersForLayer = CqlFilterHelper.getFilters(otherFilters).get(selectedTab.layerId);
        const data: FilterDialogData = {
          tabSourceId: selectedTab.tabSourceId,
          columnName: $event.columnId,
          layerId: selectedTab.layerId,
          filter: attributeFilterModel,
          columnType: $event.attributeType,
          cqlFilters: filtersForLayer,
          applicationId,
          attributeAlias,
          featureType: selectedData?.featureType,
        };
        this.dialog.open(AttributeListFilterComponent, { data, maxHeight: CssHelper.MAX_SCREEN_HEIGHT });
      });
  }

  public reloadData() {
    this.store$.select(selectSelectedTab)
      .pipe(take(1))
      .subscribe(tab => {
        if (!tab || !tab.layerId) {
          return;
        }
        this.attributeListDataService.loadData(tab.id);
      });
  }

  public loadFeatureDetailsForFeature($event: string) {
    this.attributeListFeatureDetailsService.loadFeatureDetailsForFeature($event);
  }

  public showStatisticsHelp() {
    SnackBarMessageComponent.open$(this.snackBar, {
      message: StatisticsHelper.getStatisticsHelpMessage(),
      duration: 5000,
    });
    return;
  }

  public loadStatisticsForColumn($event: { type: StatisticType; columnName: string; dataType: string }) {
    this.attributeListStatisticsService.loadStatistics($event);
  }

  public onSetAllRowsChecked($event: { checked: boolean }) {
    this.store$.select(selectSelectedTab)
      .pipe(take(1))
      .subscribe(tab => {
        if (!tab || !tab.layerId) {
          return;
        }
        this.attributeListDataService.updateAllRowsChecked(tab.id, tab.selectedDataId, $event.checked);
      });
  }

  public onSetRowChecked($event: { id: string; checked: boolean }) {
    this.store$.select(selectSelectedTab)
      .pipe(take(1))
      .subscribe(tab => {
        if (!tab || !tab.layerId) {
          return;
        }
        this.attributeListDataService.updateRowChecked(tab.id, tab.selectedDataId, $event.id, $event.checked);
      });
  }

}
